import React, { useState, useEffect } from 'react';
import DOMPurify from 'dompurify';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { 
  Mail, Send, Eye, Loader2, CheckCircle, XCircle, RefreshCw
} from 'lucide-react';
import { toast } from 'sonner';
import authAxios, { API } from '../../utils/authAxios';

export default function AdminEmailCampaign() {
  var [emails, setEmails] = useState([]);
  var [stats, setStats] = useState({ total: 0, sent: 0, pending: 0 });
  var [loading, setLoading] = useState(true);
  var [sending, setSending] = useState(false);
  var [previewHtml, setPreviewHtml] = useState('');
  var [showPreview, setShowPreview] = useState(false);
  var [selectedType, setSelectedType] = useState('centro');

  useEffect(function() {
    loadEmails();
  }, []);

  async function loadEmails() {
    setLoading(true);
    try {
      var res = await authAxios.get(API + '/admin/pending-emails', { withCredentials: true });
      setEmails(res.data.emails || []);
      setStats(res.data.stats || { total: 0, sent: 0, pending: 0 });
    } catch (e) {
      console.error(e);
    }
    setLoading(false);
  }

  async function previewEmail(type) {
    try {
      var res = await authAxios.get(API + '/admin/preview-email?type=' + type, { withCredentials: true });
      setPreviewHtml(res.data.html);
      setShowPreview(true);
    } catch (e) {
      toast.error('Error generando preview');
    }
  }

  async function sendPendingEmails() {
    setSending(true);
    try {
      var res = await authAxios.post(API + '/admin/send-pending-emails', {}, { withCredentials: true });
      toast.success('Emails enviados: ' + res.data.sent + ' de ' + res.data.total);
      loadEmails();
    } catch (e) {
      toast.error('Error enviando emails');
    }
    setSending(false);
  }

  if (loading) {
    return (
      <div className="flex justify-center py-20">
        <Loader2 className="w-8 h-8 animate-spin text-emerald-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Stats */}
      <div className="grid grid-cols-3 gap-4">
        <Card className="bg-slate-900 border-slate-800">
          <CardContent className="pt-6">
            <div className="text-center">
              <p className="text-3xl font-bold text-emerald-400">{stats.sent}</p>
              <p className="text-slate-400 text-sm">Enviados</p>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-slate-900 border-slate-800">
          <CardContent className="pt-6">
            <div className="text-center">
              <p className="text-3xl font-bold text-yellow-400">{stats.pending}</p>
              <p className="text-slate-400 text-sm">Pendientes</p>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-slate-900 border-slate-800">
          <CardContent className="pt-6">
            <div className="text-center">
              <p className="text-3xl font-bold text-slate-300">{stats.total}</p>
              <p className="text-slate-400 text-sm">Total</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Actions */}
      <Card className="bg-slate-900 border-slate-800">
        <CardHeader>
          <CardTitle className="text-slate-50 flex items-center gap-2">
            <Mail className="w-5 h-5" />
            Acciones
          </CardTitle>
        </CardHeader>
        <CardContent className="flex flex-wrap gap-3">
          <Button 
            onClick={sendPendingEmails}
            disabled={sending || stats.pending === 0}
            className="bg-emerald-600 hover:bg-emerald-700"
          >
            {sending ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Send className="w-4 h-4 mr-2" />}
            Enviar Emails Pendientes ({stats.pending})
          </Button>
          <Button 
            onClick={function() { previewEmail(selectedType); }}
            variant="outline"
            className="border-slate-700 text-slate-300"
          >
            <Eye className="w-4 h-4 mr-2" />
            Preview Email
          </Button>
          <Button 
            onClick={loadEmails}
            variant="outline"
            className="border-slate-700 text-slate-300"
          >
            <RefreshCw className="w-4 h-4 mr-2" />
            Actualizar
          </Button>
          <select
            value={selectedType}
            onChange={function(e) { setSelectedType(e.target.value); }}
            className="px-3 py-2 bg-slate-800 border border-slate-700 rounded-md text-slate-300"
          >
            <option value="centro">Email Centro</option>
            <option value="profesional">Email Profesional</option>
            <option value="taller">Email Taller</option>
          </select>
        </CardContent>
      </Card>

      {/* Preview Modal */}
      {showPreview && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-3xl w-full max-h-[80vh] overflow-auto">
            <div className="sticky top-0 bg-slate-900 p-4 flex justify-between items-center">
              <h3 className="text-white font-medium">Preview de Email</h3>
              <Button variant="ghost" onClick={function() { setShowPreview(false); }} className="text-white">
                ✕
              </Button>
            </div>
            <div dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(previewHtml || '', { ADD_ATTR: ['target', 'rel'] }) }} />
          </div>
        </div>
      )}

      {/* Pending Emails */}
      <Card className="bg-slate-900 border-slate-800">
        <CardHeader>
          <CardTitle className="text-slate-50">Emails Pendientes</CardTitle>
        </CardHeader>
        <CardContent>
          {emails.length === 0 ? (
            <p className="text-slate-400 text-center py-8">No hay emails pendientes</p>
          ) : (
            <div className="space-y-3 max-h-[400px] overflow-y-auto">
              {emails.map(function(email, idx) {
                return (
                  <div key={idx} className="p-3 bg-slate-800/50 rounded-lg border border-slate-700 flex items-center justify-between">
                    <div>
                      <p className="text-slate-300 font-medium">{email.to_email}</p>
                      <p className="text-slate-500 text-sm">{email.subject}</p>
                      <p className="text-slate-500 text-xs">
                        {email.entity_type} - {new Date(email.created_at).toLocaleString('es-CL')}
                      </p>
                    </div>
                    <div>
                      {email.status === 'sent' ? (
                        <CheckCircle className="w-5 h-5 text-emerald-500" />
                      ) : email.status === 'failed' ? (
                        <XCircle className="w-5 h-5 text-red-500" />
                      ) : (
                        <span className="text-yellow-400 text-xs">Pendiente</span>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
